import { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Brush,
} from "recharts";
import { cn } from "@/lib/utils";

export interface ChartDataPoint {
  timestamp: string;
  value: number;
  count?: number | null;
}

export interface SimpleBarChartProps {
  data: ChartDataPoint[];
  className?: string;
  height?: number;
  formatValue?: (value: number) => string;
  formatYAxis?: (value: number) => string;
  formatLabel?: (timestamp: string) => string;
  barColor?: string;
}

function defaultFormatValue(value: number): string {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  return value.toFixed(0);
}

function defaultFormatLabel(timestamp: string): string {
  const date = new Date(timestamp);
  return `${date.getMonth() + 1}/${date.getDate()}`;
}

export function SimpleBarChart({
  data,
  className,
  height = 250,
  formatValue = defaultFormatValue,
  formatYAxis,
  formatLabel = defaultFormatLabel,
  barColor = "#3b82f6", // blue-500
}: SimpleBarChartProps) {
  // Use compact format for Y-axis if not provided
  const yAxisFormatter = formatYAxis || defaultFormatValue;
  const chartData = useMemo(
    () =>
      data.map((point) => ({
        ...point,
        label: formatLabel(point.timestamp),
      })),
    [data, formatLabel]
  );

  if (!data || data.length === 0) {
    return (
      <div
        className={cn("flex items-center justify-center text-muted-foreground", className)}
        style={{ height }}
      >
        No data available
      </div>
    );
  }

  // Show brush (zoom) control only when there are many data points
  const showBrush = data.length > 14;

  return (
    <div className={cn("w-full", className)} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }}
            tickLine={false}
            axisLine={{ stroke: "hsl(var(--border))" }}
            interval="preserveStartEnd"
          />
          <YAxis
            tickFormatter={yAxisFormatter}
            tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
            tickLine={false}
            axisLine={false}
            width={80}
          />
          <Tooltip
            formatter={(value) => [formatValue(Number(value)), "Value"]}
            labelFormatter={(label) => `Date: ${label}`}
            contentStyle={{
              backgroundColor: "rgba(255, 255, 255, 0.95)",
              border: "1px solid hsl(var(--border))",
              borderRadius: "6px",
              fontSize: "12px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
            }}
          />
          <Bar
            dataKey="value"
            fill={barColor}
            radius={[4, 4, 0, 0]}
            maxBarSize={50}
          />
          {showBrush && (
            <Brush
              dataKey="label"
              height={30}
              stroke="#3b82f6"
              fill="#f1f5f9"
              travellerWidth={10}
              tickFormatter={() => ""}
            />
          )}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export interface DataTableProps {
  data: ChartDataPoint[];
  valueLabel?: string;
  dateLabel?: string;
  countLabel?: string;
  formatValue?: (value: number) => string;
  formatLabel?: (timestamp: string) => string;
  className?: string;
}

export function DataTable({
  data,
  valueLabel = "Value",
  dateLabel = "Date",
  countLabel = "Count",
  formatValue = defaultFormatValue,
  formatLabel = defaultFormatLabel,
  className,
}: DataTableProps) {
  if (!data || data.length === 0) {
    return (
      <div className={cn("text-muted-foreground text-center py-4", className)}>
        No data available
      </div>
    );
  }

  return (
    <div className={cn("overflow-auto max-h-64", className)}>
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-background border-b">
          <tr>
            <th className="text-left py-2 px-2">{dateLabel}</th>
            <th className="text-right py-2 px-2">{valueLabel}</th>
            {data[0]?.count !== undefined && (
              <th className="text-right py-2 px-2">{countLabel}</th>
            )}
          </tr>
        </thead>
        <tbody>
          {data.map((point, index) => (
            <tr key={index} className="border-b">
              <td className="py-2 px-2">{formatLabel(point.timestamp)}</td>
              <td className="text-right py-2 px-2 font-medium">
                {formatValue(point.value)}
              </td>
              {point.count !== undefined && (
                <td className="text-right py-2 px-2">{point.count}</td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
